/* eslint-disable import/prefer-default-export */
import _get from 'lodash/get'
import _isNumber from 'lodash/isNumber'
import _parseInt from 'lodash/parseInt'
import _startsWith from 'lodash/startsWith'
import { Map, List, fromJS } from 'immutable'
import {
  client as apolloClient,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
  APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
} from '../apollo-graphql/index'
import {
  getVideos,
  getSeries,
  getTerms,
  getPromotions,
  getProducts,
} from '../node/index'
import { CONTENT_IMAGE_LIST } from '../content/index'
import { TYPE_CONTENT_PRODUCT } from '../content-type/index'
import { URL_TOPICS } from '../url/constants'
import { setMultiplePmSections as setMultiplePmSectionsAction } from '../pm-section/actions'
import { setMultiplePmLists as setMultiplePmListsAction } from '../pm-list/actions'
import {
  getPmScreenProcessing,
  getPmScreenErrorCreated,
  getPmScreenDataCreated,
} from '.'
import {
  setPmScreen as setPmScreenAction,
  setPmScreenError as setPmScreenErrorAction,
  setPmScreenProcessing as setPmScreenProcessingAction,
} from './actions'
import GET_PM_SCREEN_PERSONALIZED from './query'

/**
 * The time to wait on error before getting a screen again
 */
const GET_PM_SCREEN_ERROR_CACHE_TIME = (300 * 1000)

/**
 * The time to wait on data before getting a screen again
 */
const GET_PM_SCREEN_DATE_CACHE_TIME = (60 * 1000)

/**
 * Get a page manager screen and dispatch actions
 * @param {Object} options The options
 * @param {import('redux').Dispatch} options.dispatch A redux dispatch function
 * @param {Map} options.auth The auth state
 * @param {Map} options.pmScreen The page manager screen state
 * @param {String} options.screenType The page manager screen type
 * @param {String} options.language The requested language
 * @param {Number} [options.sectionsLimit] Limit for initial sections with details (default: 4)
 * @param {Date} options.now The current date
 */
export async function getPmScreen (options = {}) {
  const {
    auth = Map(),
    pmScreen = Map(),
    dispatch,
    screenType,
    language,
    sectionsLimit = 4,
    now = new Date(),
    resolver = Map(),
  } = options
  try {
    const processing = getPmScreenProcessing({ pmScreen, screenType, language })
    const errorCreated = getPmScreenErrorCreated({ pmScreen, screenType, language })
    const dataCreated = getPmScreenDataCreated({ pmScreen, screenType, language })
    // Don't get page manager screen if already processing
    if (processing) {
      return
    }
    // Don't get page manager screen if there was an error recently
    if (errorCreated && now.getTime() < (errorCreated.getTime() + GET_PM_SCREEN_ERROR_CACHE_TIME)) {
      return
    }
    // Don't get page manager data if data was fetch recently
    if (dataCreated && now.getTime() < (dataCreated.getTime() + GET_PM_SCREEN_DATE_CACHE_TIME)) {
      return
    }
    // SSR-seeded Member Home: identity already matches; skip remount GET.
    if (pmScreen.getIn([screenType, language, 'ssrHydrated'])) {
      return
    }
    dispatch(setPmScreenProcessingAction(screenType, language, true))
    const authToken = auth.get('jwt')

    const { data, error } = await apolloClient.query({
      query: GET_PM_SCREEN_PERSONALIZED,
      variables: {
        screenType,
        // GraphQL schema expects language as [String] array
        language: language ? [language] : undefined,
        sectionsLimit: sectionsLimit || undefined,
        // Resolved Content on first-touch tab rows (same locale shape as legacy tile query).
        contentLanguage: language ? [language, '_primary'] : undefined,
        imageType: CONTENT_IMAGE_LIST,
      },
      fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
      errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
      context: {
        jwt: authToken,
      },
    })

    if (error) {
      throw error
    }

    const pmScreenData = _get(data, ['pmScreenPersonalized', 'pmScreen'])
    if (!pmScreenData) {
      throw new Error('No pmScreen data returned')
    }

    // Extract section IDs from pmSections (300 limit, only id field)
    // pmScreen.pmSections returns ALL sections ordered by 'order' field
    // (this is the source of truth)
    // These sectionIds are used for scroll loading - all remaining sections
    // can be loaded on scroll
    const pmSections = _get(pmScreenData, 'pmSections', [])
    const sectionIds = List(pmSections.map((section) => section.id))

    // Set pmScreen data with sectionIds (preserving order from pmScreen.pmSections)
    // This contains ALL sections (up to 300) - not just the preloaded ones
    const screenData = {
      ...pmScreenData,
      sectionIds: sectionIds.toArray(),
    }
    // Action expects plain object, reducer will convert to Immutable
    dispatch(setPmScreenAction(screenType, language, screenData))

    // Extract sections with details from the alias (optimized - fetched in same query)
    // pmSectionsWithDetails is ONLY for initial preload (limited by $sectionsLimit)
    // The full sectionIds list above is used for scroll loading all remaining sections
    const sectionsWithDetails = _get(pmScreenData, 'pmSectionsWithDetails', [])
    if (sectionsWithDetails.length > 0) {
      // Extract all pmLists from sections for separate dispatch
      const allPmLists = []
      sectionsWithDetails.forEach((section) => {
        if (section.pmList && section.pmList.length > 0) {
          allPmLists.push(...section.pmList)
        }
      })
      if (allPmLists.length > 0) {
        dispatch(setMultiplePmListsAction({
          lists: allPmLists,
          processing: false,
        }))
      }

      // Store sections directly from GraphQL - reducer will handle structure
      // Sections are already in correct order from pmSectionsWithDetails
      // Filter out sections without app data (they may not be personalized/visible)
      const sectionsToStore = sectionsWithDetails
        .filter((section) => section.app) // Only store sections with app data
        .map((section) => ({
          id: section.id,
          app: section.app,
          pmList: section.pmList,
        }))

      if (sectionsToStore.length > 0) {
        dispatch(setMultiplePmSectionsAction({
          sections: sectionsToStore,
          processing: false,
          done: true,
        }))

        // Defer Brooklyn node fan-out until after first paint so the first four
        // GraphQL sections can render without competing for the network.
        const deferredSections = sectionsWithDetails
        const deferredLanguage = language
        const deferredResolver = resolver
        setTimeout(() => {
          loadPmSectionTabNodes({
            dispatch,
            sectionsWithDetails: deferredSections,
            language: deferredLanguage,
            resolver: deferredResolver,
          })
        }, 0)
      }
    }

    // Set processing to false after completion
    dispatch(setPmScreenProcessingAction(screenType, language, false))
  } catch (e) {
    dispatch(setPmScreenErrorAction(screenType, language, e))
    dispatch(setPmScreenProcessingAction(screenType, language, false))
  }
}

function loadPmSectionTabNodes ({
  dispatch,
  sectionsWithDetails,
  language,
  resolver,
}) {
  sectionsWithDetails.forEach((section) => {
    const appData = section.app || {}
    const sectionDataForTabs = appData.data || {}
    const tabs = sectionDataForTabs.tabs || []
    const contentList = tabs.flatMap((tab) => tab.tabContent || [])

    const videosList = contentList.filter((v) => v.contentType === 'video')
    if (videosList.length > 0) {
      const ids = videosList.map((v) => v.contentId).filter(Boolean)
      if (ids.length > 0) {
        dispatch(getVideos({ ids, language, preventNodeFetch: false }))
      }
    }

    const seriesList = contentList.filter((v) => v.contentType === 'series')
    if (seriesList.length > 0) {
      const ids = seriesList.map((v) => Number(v.contentId)).filter(Boolean)
      if (ids.length > 0) {
        dispatch(getSeries({ ids, language, preventNodeFetch: false }))
      }
    }

    const promotions = contentList.filter((v) => v.contentType === 'promotion')
    if (promotions.length > 0) {
      const ids = promotions.map((v) => v.contentId).filter(Boolean)
      if (ids.length > 0) {
        dispatch(getPromotions({ ids, language, preventNodeFetch: false }))
      }
    }

    const products = contentList.filter((v) => v.contentType === TYPE_CONTENT_PRODUCT)
    if (products.length > 0) {
      const ids = products.map((v) => v.contentId).filter(Boolean)
      if (ids.length > 0) {
        dispatch(getProducts({ ids, language, preventNodeFetch: false }))
      }
    }

    const tagsList = contentList.filter((v) => v.contentType === 'tag')
    if (tagsList.length > 0) {
      const ids = tagsList.map((v) => Number(v.contentId)).filter(Boolean)
      if (ids.length > 0) {
        dispatch(getTerms({
          ids, language, fetchAsNodes: true, contentType: 'tag',
        }))
      }
    }

    const sectionAppData = section.app || {}
    const sectionDataForFooter = sectionAppData.data || {}
    const sectionDataImmutable = fromJS(sectionDataForFooter)
    const parentTermId = sectionDataImmutable.getIn(['footer', 'label'])
      ? _parseInt(sectionDataImmutable.getIn(['footer', 'label']), 10)
      : undefined
    const pageUrl = resolver.get('path')
    if (parentTermId && _isNumber(parentTermId) && _startsWith(pageUrl, URL_TOPICS)) {
      dispatch(getTerms({
        ids: [parentTermId], language, fetchAsNodes: true, contentType: 'tag',
      }))
    }
  })
}
